import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import type { CameraDevice } from '@fastgarage/protocol';
import type { ConnectionUiState } from '../livekit';
import type { UploadStatus } from '../uploadQueue';

export interface PointerEvent2D {
  x: number;
  y: number;
  /** Timestamp — a new value restarts the overlay even at the same position. */
  at: number;
}

export interface HintEvent {
  text: string;
  at: number;
}

export interface CameraScreenProps {
  localStream: MediaStream | null;
  kennzeichen: string;
  connection: ConnectionUiState;
  staffConnected: boolean;
  uploadStatus: UploadStatus;
  pointer: PointerEvent2D | null;
  hint: HintEvent | null;
  /** Every video input of the device, incl. an attached USB camera. */
  cameras: CameraDevice[];
  activeCameraId: string | null;
  onSelectCamera(deviceId: string): void;
  /** Registers the preview <video> with the runtime for canvas captures. */
  onVideoElement(el: HTMLVideoElement | null): void;
  onBeenden(): void;
}

const POINTER_VISIBLE_MS = 2_500;
const HINT_VISIBLE_MS = 6_000;

/** Where the (object-fit: cover) video frame actually lies within the element. */
function coverRect(
  videoWidth: number,
  videoHeight: number,
  elementWidth: number,
  elementHeight: number,
): { left: number; top: number; width: number; height: number } {
  const vw = videoWidth || 1280;
  const vh = videoHeight || 720;
  const scale = Math.max(elementWidth / vw, elementHeight / vh);
  const width = vw * scale;
  const height = vh * scale;
  return {
    left: (elementWidth - width) / 2,
    top: (elementHeight - height) / 2,
    width,
    height,
  };
}

function connectionLabel(connection: ConnectionUiState, staffConnected: boolean): string {
  if (connection === 'wiederverbinden') return 'Verbindung wird wiederhergestellt…';
  if (connection === 'getrennt') return 'Getrennt';
  return staffConnected ? 'Verbunden' : 'Warten auf Mitarbeiter…';
}

function PointerRing(props: {
  pointer: PointerEvent2D;
  videoRef: RefObject<HTMLVideoElement>;
}): JSX.Element | null {
  const [style, setStyle] = useState<CSSProperties | null>(null);

  useEffect(() => {
    const video = props.videoRef.current;
    if (!video) return;
    const rect = coverRect(
      video.videoWidth,
      video.videoHeight,
      video.clientWidth,
      video.clientHeight,
    );
    setStyle({
      left: `${rect.left + props.pointer.x * rect.width}px`,
      top: `${rect.top + props.pointer.y * rect.height}px`,
    });
    const timer = setTimeout(() => setStyle(null), POINTER_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [props.pointer, props.videoRef]);

  if (!style) return null;
  return <div key={props.pointer.at} className="pointer-ring" style={style} aria-hidden="true" />;
}

function HintBanner(props: { hint: HintEvent }): JSX.Element | null {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setVisible(true);
    const timer = setTimeout(() => setVisible(false), HINT_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [props.hint.at]);

  if (!visible) return null;
  return (
    <div className="hint-banner" role="status">
      {props.hint.text}
    </div>
  );
}

/** Fullscreen dark camera view: local preview, status bar, chips, overlays. */
export function CameraScreen(props: CameraScreenProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { onVideoElement, localStream } = props;

  useEffect(() => {
    const video = videoRef.current;
    onVideoElement(video);
    if (video && localStream && video.srcObject !== localStream) {
      video.srcObject = localStream;
      void video.play().catch(() => undefined);
    }
    return () => onVideoElement(null);
  }, [onVideoElement, localStream]);

  const { transferred, queued } = props.uploadStatus;

  return (
    <div className="camera-screen">
      <video
        ref={videoRef}
        className="camera-preview"
        autoPlay
        muted
        playsInline
        disablePictureInPicture
      />

      <div className="camera-topbar">
        <span
          className={`conn-dot ${props.connection === 'verbunden' && props.staffConnected ? 'conn-ok' : 'conn-warn'}`}
          aria-hidden="true"
        />
        <span className="conn-text">{connectionLabel(props.connection, props.staffConnected)}</span>
        <span className="topbar-kennzeichen">{props.kennzeichen}</span>
      </div>

      {props.hint ? <HintBanner hint={props.hint} /> : null}
      {props.pointer ? <PointerRing pointer={props.pointer} videoRef={videoRef} /> : null}

      <div className="camera-bottombar">
        {props.cameras.length > 1 ? (
          <div className="camera-picker">
            <label htmlFor="camera-select">Kamera</label>
            <select
              id="camera-select"
              value={props.activeCameraId ?? ''}
              onChange={(event) => props.onSelectCamera(event.target.value)}
            >
              {props.activeCameraId === null ? <option value="">Wird ermittelt…</option> : null}
              {props.cameras.map((camera) => (
                <option key={camera.deviceId} value={camera.deviceId}>
                  {camera.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="chips">
          <span className="chip">
            {transferred} {transferred === 1 ? 'Foto' : 'Fotos'} übertragen
          </span>
          {queued > 0 ? <span className="chip chip-queue">{queued} in Warteschlange</span> : null}
        </div>
        <button type="button" className="btn-end" onClick={props.onBeenden}>
          Beenden
        </button>
      </div>
    </div>
  );
}
